import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Lock, User } from "lucide-react";
import { api, imgUrl, formatPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Checkout() {
  const { items, subtotal, clearCart } = useCart();
  const { user, loading, openAuth } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ customerName: "", phone: "", address: "" });
  const [payment, setPayment] = useState("cash_on_delivery");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user && user.name) setForm((f) => ({ ...f, customerName: f.customerName || user.name }));
  }, [user]);

  const shipping = subtotal > 4999 || subtotal === 0 ? 0 : 99;
  const total = subtotal + shipping;

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const placeOrder = async (e) => {
    e.preventDefault();
    if (!user || !user.role) {
      openAuth({ mode: "login", reason: "Please sign in to place your order." });
      return;
    }
    if (!form.customerName || !form.phone || !form.address) {
      toast.error("Please fill in all details");
      return;
    }
    if (!/^\d{10}$/.test(form.phone.replace(/\s/g, ""))) {
      toast.error("Enter a valid 10-digit phone number");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders", {
        ...form,
        items: items.map((i) => ({
          productId: i.productId,
          name: i.name,
          price: i.price,
          size: i.size,
          quantity: i.quantity,
          image: i.image,
        })),
        subtotal,
        total,
        paymentMethod: payment,
      });
      clearCart();
      navigate("/order-confirmation", { state: { order: data } });
    } catch (err) {
      toast.error("Could not place order. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || user === null) {
    return (
      <div className="pt-40 pb-32 text-center min-h-screen" data-testid="checkout-loading">
        <p className="font-mono-th text-sm text-neutral-500 uppercase tracking-[0.2em]">Loading...</p>
      </div>
    );
  }

  if (!user || !user.role) {
    return (
      <div className="pt-40 pb-32 min-h-screen flex items-center justify-center px-5" data-testid="checkout-auth-gate">
        <div className="glass p-8 md:p-12 max-w-md w-full text-center">
          <div className="w-12 h-12 border border-white/20 flex items-center justify-center mx-auto mb-6">
            <Lock className="w-5 h-5" />
          </div>
          <h1 className="font-display font-black text-3xl uppercase">Sign in to checkout</h1>
          <p className="text-neutral-400 text-sm mt-3">
            You need an account to place an order. Sign in or create one — it takes a few seconds.
          </p>
          <button
            onClick={() => openAuth({ mode: "login", reason: "Please sign in to place your order.", onSuccess: () => {} })}
            className="mt-8 w-full bg-white text-black py-4 font-mono-th text-xs uppercase tracking-[0.2em] font-semibold hover:bg-neutral-200 transition-colors flex items-center justify-center gap-2"
            data-testid="checkout-signin-btn"
          >
            <User className="w-4 h-4" /> Sign In / Create Account
          </button>
          <button
            onClick={() => navigate("/shop")}
            className="mt-3 w-full border border-white/15 py-4 font-mono-th text-xs uppercase tracking-[0.2em] hover:bg-white/5"
            data-testid="checkout-gate-shop-btn"
          >
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="pt-40 pb-32 text-center min-h-screen" data-testid="checkout-empty">
        <p className="font-display text-3xl">Your bag is empty</p>
        <button onClick={() => navigate("/shop")} className="mt-6 border border-white/20 px-6 py-3 font-mono-th text-xs uppercase tracking-[0.2em]">Continue shopping</button>
      </div>
    );
  }

  return (
    <div className="pt-28 md:pt-36 pb-20 min-h-screen" data-testid="checkout-page">
      <div className="max-w-[1100px] mx-auto px-5 md:px-10">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 font-mono-th text-xs uppercase tracking-[0.2em] text-neutral-400 hover:text-white mb-8">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <h1 className="font-display font-black uppercase text-4xl md:text-6xl tracking-tight mb-12">Checkout</h1>

        <div className="grid lg:grid-cols-[1fr_400px] gap-12">
          <form onSubmit={placeOrder} className="space-y-8" data-testid="checkout-form">
            <div>
              <p className="font-mono-th text-xs uppercase tracking-[0.25em] text-neutral-500 mb-5">Shipping Details</p>
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-neutral-400 mb-2 block">Full Name</label>
                  <input value={form.customerName} onChange={update("customerName")} className="w-full bg-[#13151A] border border-white/10 px-4 py-3.5 text-sm focus:outline-none focus:border-white/30" placeholder="Your name" data-testid="checkout-name" />
                </div>
                <div>
                  <label className="text-xs text-neutral-400 mb-2 block">Phone Number</label>
                  <input value={form.phone} onChange={update("phone")} className="w-full bg-[#13151A] border border-white/10 px-4 py-3.5 text-sm focus:outline-none focus:border-white/30" placeholder="10-digit mobile number" data-testid="checkout-phone" />
                </div>
                <div>
                  <label className="text-xs text-neutral-400 mb-2 block">Delivery Address</label>
                  <textarea value={form.address} onChange={update("address")} rows={4} className="w-full bg-[#13151A] border border-white/10 px-4 py-3.5 text-sm focus:outline-none focus:border-white/30 resize-none" placeholder="House no, street, city, state, pincode" data-testid="checkout-address" />
                </div>
              </div>
            </div>

            <div>
              <p className="font-mono-th text-xs uppercase tracking-[0.25em] text-neutral-500 mb-5">Payment Method</p>
              <div className="space-y-3">
                {[
                  { v: "cash_on_delivery", l: "Cash on Delivery" },
                  { v: "upi", l: "UPI / Online (coming soon)" },
                  { v: "card", l: "Card (coming soon)" },
                ].map((p) => (
                  <label key={p.v} className={`flex items-center gap-3 border p-4 cursor-pointer ${payment === p.v ? "border-white/40 bg-white/5" : "border-white/10"} ${p.v !== "cash_on_delivery" ? "opacity-50" : ""}`} data-testid={`payment-${p.v}`}>
                    <input type="radio" name="payment" disabled={p.v !== "cash_on_delivery"} checked={payment === p.v} onChange={() => setPayment(p.v)} className="accent-white" />
                    <span className="text-sm">{p.l}</span>
                  </label>
                ))}
              </div>
            </div>
          </form>

          {/* Summary */}
          <div className="lg:sticky lg:top-28 h-fit">
            <div className="glass p-6" data-testid="order-summary">
              <p className="font-mono-th text-xs uppercase tracking-[0.25em] text-neutral-500 mb-5">Order Summary</p>
              <div className="space-y-4 max-h-64 overflow-y-auto">
                {items.map((it) => (
                  <div key={`${it.productId}-${it.size}`} className="flex gap-3">
                    <img src={imgUrl(it.image)} alt={it.name} className="w-14 h-16 object-cover bg-[#1A1D24]" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium line-clamp-1">{it.name}</p>
                      <p className="font-mono-th text-[10px] text-neutral-500">SIZE {it.size} · QTY {it.quantity}</p>
                      <p className="text-xs font-semibold mt-1">{formatPrice(it.price * it.quantity)}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="border-t border-white/10 mt-5 pt-5 space-y-3 text-sm">
                <div className="flex justify-between text-neutral-400"><span>Subtotal</span><span>{formatPrice(subtotal)}</span></div>
                <div className="flex justify-between text-neutral-400"><span>Shipping</span><span>{shipping === 0 ? "Free" : formatPrice(shipping)}</span></div>
                <div className="flex justify-between font-display font-bold text-lg pt-3 border-t border-white/10"><span>Total</span><span data-testid="checkout-total">{formatPrice(total)}</span></div>
              </div>
              <motion.button
                whileTap={{ scale: 0.97 }}
                onClick={placeOrder}
                disabled={submitting}
                className="w-full mt-6 bg-white text-black py-4 font-mono-th text-xs uppercase tracking-[0.2em] font-semibold flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-neutral-200 transition-colors"
                data-testid="place-order-btn"
              >
                <Lock className="w-4 h-4" /> {submitting ? "Placing Order..." : "Place Order"}
              </motion.button>
              <p className="text-[10px] text-neutral-600 text-center mt-3 font-mono-th">Free shipping over ₹4,999</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
